import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Book, MagnifyingGlass, Feather, ArrowLeft, FunnelSimple, X } from '@phosphor-icons/react';
import { proxyImage } from '../lib/imageProxy';
import GlobalHeader from '../components/GlobalHeader';

export const Authors = () => {
  const [searchParams] = useSearchParams();
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState(searchParams.get('search') || '');
  const [type, setType] = useState('legendary');
  const [era, setEra] = useState('');
  const [genre, setGenre] = useState('');
  const [region, setRegion] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ eras: [], genres: [], regions: [] });

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/authors/filters`)
      .then((res) => setFilters(res.data))
      .catch(() => {});
  }, []);

  const fetchAuthors = async (opts = {}) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ author_type: opts.type ?? type, limit: '200' });
      const q = opts.query ?? query;
      const e = opts.era ?? era;
      const g = opts.genre ?? genre;
      const r = opts.region ?? region;
      if (q) params.set('search', q);
      if (e) params.set('era', e);
      if (g) params.set('genre', g);
      if (r) params.set('region', r);
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/authors?${params.toString()}`);
      setAuthors(res.data);
    } catch (error) {
      console.error('Error fetching authors:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuthors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, era, genre, region]);

  const clearFilters = () => {
    setEra(''); setGenre(''); setRegion(''); setQuery('');
  };

  const activeFilterCount = [era, genre, region].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-background">
      <GlobalHeader />

      <div className="container mx-auto px-6 py-12">
        <div className="max-w-6xl mx-auto">
          <Link to="/" data-testid="back-button" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors">
            <ArrowLeft size={16} />
            Back to Home
          </Link>

          <div className="flex items-center gap-3 mb-3">
            <Feather size={40} weight="duotone" className="text-primary" />
            <h2 className="text-5xl md:text-6xl tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 700 }}>
              Literary Heritage
            </h2>
          </div>
          <p className="text-muted-foreground text-lg mb-10 max-w-3xl">
            Explore the timeless works of Hindi literature's greatest voices. From medieval saints to modern rebels, discover the poets and writers who shaped our literary consciousness.
          </p>

          {/* Search & Filter */}
          <form onSubmit={(e) => { e.preventDefault(); fetchAuthors(); }} className="mb-6 flex gap-3">
            <div className="flex-1 relative">
              <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name, theme, or biography..."
                data-testid="authors-search-input"
                className="w-full pl-12 pr-4 py-3 bg-card border border-border rounded-full outline-none focus:border-primary"
              />
            </div>
            <button
              type="submit"
              data-testid="authors-search-button"
              className="bg-primary text-primary-foreground rounded-full py-3 px-8 font-semibold hover:opacity-90 transition-opacity"
            >
              Search
            </button>
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              data-testid="toggle-filters-button"
              className={`rounded-full py-3 px-5 font-semibold transition-colors border ${showFilters || activeFilterCount ? 'bg-primary/10 border-primary text-primary' : 'bg-card border-border hover:border-primary'}`}
            >
              <FunnelSimple size={20} className="inline mr-1" />
              Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
            </button>
          </form>

          {/* Filter Panel */}
          {showFilters && (
            <div className="bg-card border border-border rounded-xl p-6 mb-6" data-testid="filters-panel">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2 block">Era / Period</label>
                  <select
                    value={era}
                    onChange={(e) => setEra(e.target.value)}
                    data-testid="filter-era-select"
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary"
                  >
                    <option value="">All Eras</option>
                    {filters.eras.map((e) => <option key={e} value={e}>{e}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2 block">Genre</label>
                  <select
                    value={genre}
                    onChange={(e) => setGenre(e.target.value)}
                    data-testid="filter-genre-select"
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary"
                  >
                    <option value="">All Genres</option>
                    {filters.genres.map((g) => <option key={g} value={g}>{g}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2 block">Region</label>
                  <select
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                    data-testid="filter-region-select"
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary"
                  >
                    <option value="">All Regions</option>
                    {(filters.regions || []).map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              </div>
              {activeFilterCount > 0 && (
                <button
                  onClick={clearFilters}
                  data-testid="clear-filters-button"
                  className="mt-4 text-sm text-primary hover:underline inline-flex items-center gap-1"
                >
                  <X size={14} /> Clear all filters
                </button>
              )}
            </div>
          )}

          <div className="flex gap-3 mb-10">
            <button
              onClick={() => setType('legendary')}
              data-testid="filter-legendary"
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${type === 'legendary' ? 'bg-primary text-primary-foreground' : 'bg-card border border-border hover:border-primary'}`}
            >
              Legendary Authors
            </button>
            <button
              onClick={() => setType('contemporary')}
              data-testid="filter-contemporary"
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${type === 'contemporary' ? 'bg-primary text-primary-foreground' : 'bg-card border border-border hover:border-primary'}`}
            >
              Contemporary
            </button>
            <span className="ml-auto text-sm text-muted-foreground self-center" data-testid="authors-count">
              {loading ? 'Loading…' : `${authors.length} author${authors.length !== 1 ? 's' : ''}`}
            </span>
          </div>

          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : authors.length === 0 ? (
            <div className="bg-card border border-border rounded-lg p-16 text-center" data-testid="no-authors-state">
              <Feather size={48} weight="duotone" className="text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No authors match these filters. Try clearing them.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {authors.map((author) => (
                <Link
                  key={author.author_id}
                  to={`/author/${author.slug}`}
                  data-testid={`author-${author.author_id}`}
                  className="bg-card border border-border rounded-lg overflow-hidden hover:border-primary transition-colors group"
                >
                  <div className="aspect-[3/4] bg-muted relative overflow-hidden">
                    {author.photograph ? (
                      <img
                        src={proxyImage(author.photograph)}
                        alt={author.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Feather size={64} weight="duotone" className="text-muted-foreground" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-background/95 via-background/40 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <h3 className="text-2xl mb-1" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 700 }}>
                        {author.name}
                      </h3>
                      {author.birth_date && (
                        <p className="text-xs text-muted-foreground">
                          {String(author.birth_date).substring(0, 4)}{author.death_date ? ` – ${String(author.death_date).substring(0, 4)}` : ''}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="p-4">
                    {author.literary_movement && (
                      <span className="text-xs font-semibold uppercase tracking-widest text-primary mb-2 block">
                        {author.literary_movement}
                      </span>
                    )}
                    <p className="text-sm text-muted-foreground line-clamp-2">{author.biography ? author.biography.substring(0, 120) : ''}...</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Authors;
